import { useMemo } from 'react';
import { z } from 'zod';

import {
  FeatureConfigOptionFragment,
  InputTypeEnum,
} from '@/shared/types/types.generated';
import { optionalString, requiredString } from '@/shared/utils/validation';

export const useDynamicForm = (formConfig?: FeatureConfigOptionFragment[]) => {
  const createDynamicSchema = (formConfig?: FeatureConfigOptionFragment[]) => {
    if (!formConfig || formConfig.length === 0) {
      return z.object({}).passthrough();
    }

    const schemaObj: Record<string, z.ZodTypeAny> = {};

    formConfig.forEach((input) => {
      const isBooleanType = [
        InputTypeEnum.Checkbox,
        InputTypeEnum.Switch,
      ].includes(input.inputType as InputTypeEnum);

      if (isBooleanType) {
        if (input.required) {
          schemaObj[input.value] = z.boolean();
        } else {
          schemaObj[input.value] = z.boolean().optional();
        }
        return;
      }

      if (input.required) {
        schemaObj[input.value] = requiredString({
          regex: input.validationRegex,
        });
      } else {
        schemaObj[input.value] = optionalString({
          regex: input.validationRegex,
        });
      }
    });

    // Passthrough keeps any existing metaData keys that aren't part of the
    // current config (relevant when editing) instead of stripping them.
    // Callers that nest this under an optional `metaData` key should add
    // `.default({})` so an untouched object still passes validation.
    return z.object(schemaObj).passthrough();
  };

  const dynamicSchema = useMemo(() => {
    return createDynamicSchema(formConfig);
  }, [formConfig]);

  return {
    dynamicSchema,
    createDynamicSchema,
  };
};
